import type { QueryClient } from '@tanstack/react-query';
import { Api } from 'laravel-request';
import moment from 'moment';
import { IndexedDBCache } from 'finhelper';
import CacheHelper from '#app/helpers/Cache/CacheHelper';
import TacticsHelper from '#app/helpers/TacticsHelper';
import { QUERY_KEYS } from '#app/constants/queryKeys';

interface SalaryPlanClientLike {
  id?: number | null;
  currency_id?: number | null;
  start_enter_at_date?: string | null;
}

const fetchPlanBase = async (clientId: number) => {
  return new Promise<any>((resolve, reject) => {
    Api.get('user-plan', 'index', { user_id: clientId }).orderBy('buy_at').all(resolve, reject);
  });
};

const fetchObligations = async (clientId: number, currencyId: number) => {
  return new Promise<any>((resolve, reject) => {
    (
      Api.get('active', 'index', {
        currency_id: currencyId,
        user_id: clientId,
      }) as any
    )
      .whereObligationType(true)
      .with('payments', (query: any) => query.orderBy('paid_at', 'ASC'))
      .all(resolve, reject);
  });
};

const fetchGoals = async (clientId: number, currencyId: number) => {
  return new Promise<any>((resolve, reject) => {
    Api.get('active-goal', 'index', {
      user_id: clientId,
      currency_id: currencyId,
    })
      .with('goal_payments', (query: any) => query.with('currency').orderBy('paid_at'))
      .with('currency')
      .all(resolve, reject);
  });
};

const fetchFactIncome = async (client: SalaryPlanClientLike) => {
  const clientId = client.id as number;
  const currencyId = client.currency_id as number;
  const startEnterAtDate = client.start_enter_at_date;

  if (!startEnterAtDate) {
    return { data: { chart: [] } };
  }

  const startDate = moment(startEnterAtDate, 'DD.MM.YYYY').startOf('year').format('YYYY-MM-DD');
  const endDate = moment(startEnterAtDate, 'DD.MM.YYYY')
    .startOf('year')
    .add(100, 'years')
    .format('YYYY-MM-DD');

  const cacheKey = CacheHelper.generateKey(CacheHelper.TACTICS_MONTH, {
    clientId,
    currencyId,
    startDate,
    endDate,
  });

  const data = await IndexedDBCache.get(cacheKey);
  return TacticsHelper.loadData(
    { id: clientId, currency_id: currencyId },
    clientId,
    startDate,
    endDate,
    currencyId,
    data,
    cacheKey
  );
};

export const prefetchSalaryPlanCache = async (
  queryClient: QueryClient,
  client: SalaryPlanClientLike | null | undefined
) => {
  const clientId = client?.id;
  const currencyId = client?.currency_id;

  if (!clientId || !currencyId) {
    return;
  }

  await Promise.allSettled([
    queryClient.prefetchQuery({
      queryKey: QUERY_KEYS.plan.all(clientId),
      queryFn: () => fetchPlanBase(clientId),
    }),
    queryClient.prefetchQuery({
      queryKey: QUERY_KEYS.obligations.all(clientId, currencyId),
      queryFn: () => fetchObligations(clientId, currencyId),
    }),
    queryClient.prefetchQuery({
      queryKey: QUERY_KEYS.goals.all(clientId, currencyId),
      queryFn: () => fetchGoals(clientId, currencyId),
    }),
    queryClient.prefetchQuery({
      queryKey: QUERY_KEYS.tactics.factIncome(clientId, currencyId),
      queryFn: () => fetchFactIncome(client),
    }),
  ]);
};
